import { QUESTION_CHILD_KEYS } from "./questionTree";

function normalizeDiagnosisText(value) {
  return (value || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getDiagnosisConfigsFromNode(node) {
  if (!node) return [];

  if (Array.isArray(node.addsDiagnoses)) {
    return node.addsDiagnoses;
  }

  return node.addsDiagnosis ? [node.addsDiagnosis] : [];
}

const DEFAULT_DIAGNOSIS_ANSWER_FIELDS = [
  "answer",
  "subAnswer",
  "subAnswer2",
  "subAnswer3",
];

function getDiagnosisAnswerFields(diagnosisConfig) {
  const configuredFields =
    diagnosisConfig.answerFields || diagnosisConfig.answerField;

  if (Array.isArray(configuredFields)) {
    return configuredFields;
  }

  if (configuredFields) {
    return [configuredFields];
  }

  return DEFAULT_DIAGNOSIS_ANSWER_FIELDS;
}

function getDiagnosisSelectedValues(node, diagnosisConfig) {
  return getDiagnosisAnswerFields(diagnosisConfig)
    .reduce((selectedValues, fieldName) => {
      const value = node[fieldName];
      if (Array.isArray(value)) {
        return selectedValues.concat(value);
      }

      return selectedValues.concat(value);
    }, [])
    .filter((value) => value !== undefined && value !== null && value !== "");
}

function isDiagnosisTriggerSelected(node, diagnosisConfig) {
  if (!node) return false;

  const selectedValues = getDiagnosisSelectedValues(node, diagnosisConfig);

  if (Array.isArray(diagnosisConfig.selectedAnswers)) {
    return selectedValues.some((value) =>
      diagnosisConfig.selectedAnswers.includes(value)
    );
  }

  if (diagnosisConfig.selectedAnswer !== undefined) {
    return selectedValues.includes(diagnosisConfig.selectedAnswer);
  }

  return selectedValues.length > 0;
}

function diagnosisWasInitiallySelected(diagnosisConfig) {
  return !!(
    diagnosisConfig &&
    (diagnosisConfig.initiallySelected ||
      diagnosisConfig.diagnosisInitiallySelected)
  );
}

function collectSelectedAddedDiagnosesFromNode(node, addedDiagnoses) {
  getDiagnosisConfigsFromNode(node).forEach((diagnosisConfig) => {
    if (
      diagnosisConfig &&
      diagnosisConfig.name &&
      isDiagnosisTriggerSelected(node, diagnosisConfig) &&
      !diagnosisWasInitiallySelected(diagnosisConfig)
    ) {
      const key = normalizeDiagnosisText(diagnosisConfig.name);
      if (!addedDiagnoses[key]) {
        addedDiagnoses[key] = diagnosisConfig;
      }
    }
  });

  QUESTION_CHILD_KEYS.forEach((childKey) => {
    if (Array.isArray(node && node[childKey])) {
      node[childKey].forEach((childNode) => {
        collectSelectedAddedDiagnosesFromNode(childNode, addedDiagnoses);
      });
    }
  });
}

export function getExamAddedDiagnoses(questions) {
  const addedDiagnoses = {};

  (questions || []).forEach((question) => {
    collectSelectedAddedDiagnosesFromNode(question, addedDiagnoses);
  });

  return Object.values(addedDiagnoses);
}

function questionHasResponse(question) {
  if (!question) return false;

  if (question.type === "textarea") {
    return !!(question.answer && question.answer.trim());
  }

  return !!question.answer;
}

function normalizeRules(rules) {
  if (!rules) return [];
  return Array.isArray(rules) ? rules : [rules];
}

function normalizeRuleValue(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function getNodeValueByPath(node, path) {
  if (!node || !path) return undefined;

  return String(path)
    .split(".")
    .reduce(
      (value, key) =>
        value === undefined || value === null ? undefined : value[key],
      node
    );
}

function walkNodeTree(node, callback, inheritedSection) {
  if (!node) return;

  const section = node.section || inheritedSection;
  callback(node, section);

  QUESTION_CHILD_KEYS.forEach((childKey) => {
    if (Array.isArray(node[childKey])) {
      node[childKey].forEach((childNode) => {
        walkNodeTree(childNode, callback, section);
      });
    }
  });
}

function nodeMatchesAdditionalRemarksRule(node, section, rule) {
  if (!node || !rule) return false;

  const expectedAnswers = Array.isArray(rule.answers)
    ? rule.answers
    : [rule.answer];
  const nodeAnswer = getNodeValueByPath(node, rule.answerField || "answer");
  const answerMatches = expectedAnswers.some(
    (answer) => normalizeRuleValue(nodeAnswer) === normalizeRuleValue(answer)
  );

  const checks = [
    !rule.sectionNumber ||
      (section &&
        normalizeRuleValue(section.number) ===
          normalizeRuleValue(rule.sectionNumber)),
    !rule.sectionTitle ||
      (section &&
        normalizeRuleValue(section.title) ===
          normalizeRuleValue(rule.sectionTitle)),
    !rule.question ||
      normalizeRuleValue(node.question) === normalizeRuleValue(rule.question),
    !rule.main || normalizeRuleValue(node.main) === normalizeRuleValue(rule.main),
    !rule.matchLabel ||
      normalizeRuleValue(node.label) === normalizeRuleValue(rule.matchLabel),
    !rule.radioGroup ||
      normalizeRuleValue(node.radioGroup) === normalizeRuleValue(rule.radioGroup),
    answerMatches,
  ];

  return checks.every(Boolean);
}

function getAdditionalRemarksRuleKey(rule) {
  return (
    rule.key ||
    [
      rule.sectionNumber,
      rule.sectionTitle,
      rule.question,
      rule.main,
      rule.matchLabel,
      rule.radioGroup,
      rule.answerField || "answer",
      Array.isArray(rule.answers) ? rule.answers.join("|") : rule.answer,
    ]
      .filter(Boolean)
      .join(":")
  );
}

function getMatchedAdditionalRemarksRules(question, questions) {
  const rules = normalizeRules(
    question && question.additionalRemarksRequiredWhen
  );
  const matchedRules = [];

  rules.forEach((rule) => {
    let matched = false;

    (questions || []).some((examQuestion) => {
      walkNodeTree(examQuestion, (node, section) => {
        if (!matched && nodeMatchesAdditionalRemarksRule(node, section, rule)) {
          matched = true;
        }
      });

      return matched;
    });

    if (matched) {
      matchedRules.push(rule);
    }
  });

  return matchedRules;
}

function isAdditionalRemarksQuestion(question) {
  return !!(question && question.additionalRemarksRequiredWhen);
}

function getAdditionalRemarksKeys(question, questions) {
  return getMatchedAdditionalRemarksRules(question, questions)
    .map(getAdditionalRemarksRuleKey)
    .filter(Boolean)
    .sort();
}

function combineTextResponses(...responses) {
  return responses
    .map((response) => (response || "").trim())
    .filter(Boolean)
    .join("\n\n");
}

export function getDiagnosisRemarksPrefillSelection(question) {
  const options = question.diagnosisRemarksPrefillOptions || [];
  const selection = question.diagnosisRemarksPrefillSelection;

  if (selection !== undefined) {
    return options.some((option, index) => String(index) === selection)
      ? selection
      : "";
  }

  const index = question.diagnosisRemarksAnswer
    ? options.findIndex(
        (option) => option.value === question.diagnosisRemarksAnswer
      )
    : -1;
  return index === -1 ? "" : String(index);
}

export function syncDiagnosisRemarksPrefillSelection(question) {
  const selection = getDiagnosisRemarksPrefillSelection(question);
  question.diagnosisRemarksPrefillSelection = selection;
  question.diagnosisRemarksAnswer =
    selection === ""
      ? ""
      : question.diagnosisRemarksPrefillOptions[Number(selection)].value || "";
}

function clearDiagnosisRemarksResponse(question) {
  question.diagnosisRemarksAnswer = "";
  delete question.diagnosisRemarksPrefillSelection;
}

export function getDiagnosisRemarksCombinedAnswer(question) {
  if (
    question &&
    (question.requiredWhenExamAddsDiagnoses ||
      question.additionalRemarksRequiredWhen) &&
    question.diagnosisRemarksAnswer
  ) {
    return combineTextResponses(question.answer, question.diagnosisRemarksAnswer);
  }

  return (question && question.answer) || "";
}

function questionHasDiagnosisRemarksResponse(question) {
  return !!(
    question &&
    question.diagnosisRemarksAnswer &&
    question.diagnosisRemarksAnswer.trim()
  );
}

function isDiagnosisRemarksQuestion(question) {
  return !!(
    question &&
    question.requiredWhenExamAddsDiagnoses &&
    Array.isArray(question.diagnosisRemarksPrefillOptions)
  );
}

function isTrackedRemarksQuestion(question) {
  return (
    isDiagnosisRemarksQuestion(question) || isAdditionalRemarksQuestion(question)
  );
}

function getAddedDiagnosisKeys(questions) {
  return getExamAddedDiagnoses(questions)
    .map((diagnosis) => normalizeDiagnosisText(diagnosis.name))
    .filter(Boolean)
    .sort();
}

function diagnosisKeyListsMatch(firstList, secondList) {
  if (!Array.isArray(firstList) || firstList.length !== secondList.length) {
    return false;
  }

  return firstList.every((key, index) => key === secondList[index]);
}

export function initializeDiagnosisRemarksTracking(questions) {
  const diagnosisKeys = getAddedDiagnosisKeys(questions);

  (questions || []).forEach((question) => {
    if (!isTrackedRemarksQuestion(question)) {
      return;
    }

    const additionalRemarksKeys = getAdditionalRemarksKeys(question, questions);

    if (diagnosisKeys.length === 0) {
      question.diagnosisRemarksDiagnosisKeys = [];
    }

    if (additionalRemarksKeys.length === 0) {
      question.additionalRemarksTriggerKeys = [];
    }

    if (diagnosisKeys.length === 0 && additionalRemarksKeys.length === 0) {
      clearDiagnosisRemarksResponse(question);
      return;
    }

    if (
      diagnosisKeys.length > 0 &&
      questionHasDiagnosisRemarksResponse(question) &&
      !Array.isArray(question.diagnosisRemarksDiagnosisKeys)
    ) {
      question.diagnosisRemarksDiagnosisKeys = diagnosisKeys;
    }

    if (
      additionalRemarksKeys.length > 0 &&
      questionHasDiagnosisRemarksResponse(question) &&
      !Array.isArray(question.additionalRemarksTriggerKeys)
    ) {
      question.additionalRemarksTriggerKeys = additionalRemarksKeys;
    }
  });

  return questions;
}

export function markDiagnosisRemarksCompleted(questions, question) {
  if (!isTrackedRemarksQuestion(question)) {
    return;
  }

  const diagnosisKeys = getAddedDiagnosisKeys(questions);
  const additionalRemarksKeys = getAdditionalRemarksKeys(question, questions);

  if (diagnosisKeys.length === 0) {
    question.diagnosisRemarksDiagnosisKeys = [];
  }

  if (additionalRemarksKeys.length === 0) {
    question.additionalRemarksTriggerKeys = [];
  }

  if (diagnosisKeys.length === 0 && additionalRemarksKeys.length === 0) {
    clearDiagnosisRemarksResponse(question);
    return;
  }

  if (diagnosisKeys.length > 0 && questionHasDiagnosisRemarksResponse(question)) {
    question.diagnosisRemarksDiagnosisKeys = diagnosisKeys;
  }

  if (
    additionalRemarksKeys.length > 0 &&
    questionHasDiagnosisRemarksResponse(question)
  ) {
    question.additionalRemarksTriggerKeys = additionalRemarksKeys;
  }
}

export function syncDiagnosisRemarksForAddedDiagnoses(questions) {
  const addedDiagnoses = getExamAddedDiagnoses(questions);
  const diagnosisKeys = addedDiagnoses
    .map((diagnosis) => normalizeDiagnosisText(diagnosis.name))
    .filter(Boolean)
    .sort();
  const result = {
    addedDiagnoses,
    additionalRemarksLabels: [],
    questions,
    remarksIndex: -1,
    shouldCompleteRemarks: false,
  };

  (questions || []).forEach((question, index) => {
    if (!isTrackedRemarksQuestion(question)) {
      return;
    }

    const additionalRemarksRules = getMatchedAdditionalRemarksRules(
      question,
      questions
    );
    const additionalRemarksKeys = additionalRemarksRules
      .map(getAdditionalRemarksRuleKey)
      .filter(Boolean)
      .sort();
    const requiresDiagnosisRemarks = diagnosisKeys.length > 0;
    const requiresAdditionalRemarks = additionalRemarksKeys.length > 0;

    if (!requiresDiagnosisRemarks) {
      question.diagnosisRemarksDiagnosisKeys = [];
    }

    if (!requiresAdditionalRemarks) {
      question.additionalRemarksTriggerKeys = [];
    }

    if (!requiresDiagnosisRemarks && !requiresAdditionalRemarks) {
      clearDiagnosisRemarksResponse(question);
      return;
    }

    const trackedKeys = Array.isArray(question.diagnosisRemarksDiagnosisKeys)
      ? question.diagnosisRemarksDiagnosisKeys
      : [];
    const trackedAdditionalRemarksKeys = Array.isArray(
      question.additionalRemarksTriggerKeys
    )
      ? question.additionalRemarksTriggerKeys
      : [];
    const diagnosisSetChanged = !diagnosisKeyListsMatch(
      trackedKeys,
      diagnosisKeys
    );
    const additionalRemarksSetChanged = !diagnosisKeyListsMatch(
      trackedAdditionalRemarksKeys,
      additionalRemarksKeys
    );

    if (diagnosisSetChanged || additionalRemarksSetChanged) {
      clearDiagnosisRemarksResponse(question);
      if (diagnosisSetChanged) {
        question.diagnosisRemarksDiagnosisKeys = [];
      }
      if (additionalRemarksSetChanged) {
        question.additionalRemarksTriggerKeys = [];
      }
    }

    if (
      diagnosisSetChanged ||
      additionalRemarksSetChanged ||
      !questionHasDiagnosisRemarksResponse(question)
    ) {
      result.shouldCompleteRemarks = true;
      result.remarksIndex = index;
      result.additionalRemarksLabels = additionalRemarksRules.map(
        (rule) => rule.label || rule.main || rule.question || "selected option"
      );
    }
  });

  return result;
}

export function validateRequiredRemarksForAddedDiagnoses({
  question,
  questions,
}) {
  if (!question || !question.requiredWhenExamAddsDiagnoses) {
    if (!question || !question.additionalRemarksRequiredWhen) {
      return null;
    }

    const matchedRules = getMatchedAdditionalRemarksRules(question, questions);
    if (
      matchedRules.length === 0 ||
      questionHasDiagnosisRemarksResponse(question)
    ) {
      return null;
    }

    const labels = matchedRules.map(
      (rule) => rule.label || rule.main || rule.question || "selected option"
    );

    return {
      message:
        question.additionalRemarksValidationMessage ||
        `Please provide additional remarks because the following option was selected: ${labels.join(
          ", "
        )}.`,
      additionalRemarksLabels: labels,
    };
  }

  const addedDiagnoses = getExamAddedDiagnoses(questions);
  if (addedDiagnoses.length === 0) {
    return null;
  }

  const hasRequiredResponse = Array.isArray(
    question.diagnosisRemarksPrefillOptions
  )
    ? questionHasDiagnosisRemarksResponse(question)
    : questionHasResponse(question);

  if (hasRequiredResponse) {
    return null;
  }

  const diagnosisNames = addedDiagnoses.map((diagnosis) => diagnosis.name);

  return {
    message: `Please provide diagnosis remarks because the exam selections add the following diagnosis: ${diagnosisNames.join(
      ", "
    )}.`,
    addedDiagnoses,
  };
}

export function getAdditionalRemarksContext(question, questions) {
  if (!question || !question.additionalRemarksRequiredWhen) {
    return {
      required: false,
      labels: [],
    };
  }

  const matchedRules = getMatchedAdditionalRemarksRules(question, questions);

  return {
    required: matchedRules.length > 0,
    labels: matchedRules.map(
      (rule) => rule.label || rule.main || rule.question || "selected option"
    ),
    label: question.additionalRemarksLabel || "Additional required explanation",
    placeholder:
      question.additionalRemarksPlaceholder || "Provide an additional explanation.",
  };
}

export function getDiagnosisRemarksPrefillContext(question, questions) {
  if (
    !question ||
    !question.requiredWhenExamAddsDiagnoses ||
    !Array.isArray(question.diagnosisRemarksPrefillOptions)
  ) {
    return {
      addedDiagnoses: [],
      options: [],
    };
  }

  const addedDiagnoses = getExamAddedDiagnoses(questions);

  return {
    addedDiagnoses,
    options:
      addedDiagnoses.length > 0 ? question.diagnosisRemarksPrefillOptions : [],
  };
}
